import React, { memo, useCallback, useEffect, useMemo } from 'react'
import tw from 'twin.macro'
import { Typography } from '@mui/material'
import { useSearchParams } from 'react-router-dom'
import { ScheduledActionsTab } from 'src/components/scheduler/scheduled-actions/ScheduledActionsTab'
import { SchedulerTabs } from 'src/components/scheduler/scheduler-tabs/SchedulerTabs'
import { RetryTemplatesTab } from 'src/components/scheduler/templates/RetryTemplatesTab'
import { AdminFetchRetryConfigTemplates } from 'src/constants/actionTypes'
import { InternalPermission } from 'src/constants/internal-access/internalPermissions'
import { SchedulerTab } from 'src/constants/scheduler/schedulerTabs'
import { useAsyncDispatchWithErrorToast } from 'src/hooks/useAsyncDispatchWithErrorToast'
import { useAuthSession } from 'src/hooks/useAuthSession'
import { useHasPermission } from 'src/hooks/useHasPermission'

/**
 * Schedulers Management — a standalone admin app (reached from the Launcher), not a section of
 * the Admin Dashboard, so it renders inside AdminShell without the left rail.
 *
 * Laid out like the dashboard's list pages: an h2, then the table card — tabs, filter row,
 * grid — as one white block filling the rest of the viewport.
 *
 * Tab and filters live in the QUERY STRING rather than component state: a row click navigates to
 * the detail page, and Back must return to the same tab, partner and page.
 */
const SchedulersManagementPageComponent = () => {
  const [searchParams, setSearchParams] = useSearchParams()
  const { isAuthenticated } = useAuthSession()
  const { asyncDispatchWithErrorToast } = useAsyncDispatchWithErrorToast()

  const selectedTab = useMemo(() => {
    return searchParams.get('tab') === SchedulerTab.Templates
      ? SchedulerTab.Templates
      : SchedulerTab.ScheduledActions
  }, [searchParams])

  const partnerId = searchParams.get('partnerId')
  // Without `partners_view` the scheduled-actions list is never fetched (`ScheduledActionsTab`),
  // so a `partnerId` in the URL must not make the tab subtitle show a stale action count.
  const canViewPartners = useHasPermission(InternalPermission.PartnersView)

  // Fetched at the page level, not inside the templates tab: the count is a TAB SUBTITLE
  // (`SchedulerTabs`), so it has to load even while the scheduled-actions tab is the active one.
  useEffect(() => {
    if (!isAuthenticated) {
      return
    }

    asyncDispatchWithErrorToast(AdminFetchRetryConfigTemplates.request())
  }, [asyncDispatchWithErrorToast, isAuthenticated])

  const handleTabChange = useCallback(
    (tab: SchedulerTab) => {
      const nextParams = new URLSearchParams(searchParams)

      nextParams.set('tab', tab)

      setSearchParams(nextParams, { replace: true })
    },
    [searchParams, setSearchParams],
  )

  return (
    <div
      data-testid="schedulers-management-page"
      css={tw`flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden p-6`}
    >
      <div css={tw`mb-6 flex shrink-0 flex-row items-center justify-between gap-4`}>
        <Typography variant="h2">Schedulers Management</Typography>
      </div>
      <div css={tw`shrink-0`}>
        <SchedulerTabs
          selectedTab={selectedTab}
          hasPartner={canViewPartners && Boolean(partnerId)}
          onChange={handleTabChange}
        />
      </div>
      {selectedTab === SchedulerTab.ScheduledActions && <ScheduledActionsTab />}
      {selectedTab === SchedulerTab.Templates && <RetryTemplatesTab />}
    </div>
  )
}

export const SchedulersManagementPage = memo(SchedulersManagementPageComponent)
